import { Link } from "react-router-dom";
import { Building2, Cpu, ShoppingBag, Heart, HardHat, Sprout, Landmark, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { useContent } from "../contexts/ContentContext";

const ICON_MAP = { Building2, Cpu, ShoppingBag, Heart, HardHat, Sprout, Landmark, Manufacturing: Building2, Technology: Cpu, "Retail & Consumer": ShoppingBag, Healthcare: Heart, Infrastructure: HardHat, Agriculture: Sprout, "Financial Services": Landmark };

export default function SectorsPage() {
  const { getPage } = useContent();
  const sec = getPage("sectors", {});

  const items = sec.items || [];

  return (
    <div data-testid="sectors-page">
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{sec.hero_label || "Industry Expertise"}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-3xl">{sec.hero_headline || "Deep sector knowledge. Relevant solutions."}</h1>
          <p className="text-white/60 font-sans text-base md:text-lg mt-6 max-w-2xl leading-relaxed">{sec.hero_subtext || ""}</p>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="space-y-0">
            {items.map((sector, i) => {
              const Icon = ICON_MAP[sector.icon] || ICON_MAP[sector.label] || Building2;
              return (
                <div key={i} data-testid={`sector-detail-${i}`} className={`py-16 ${i < items.length - 1 ? 'border-b border-gray-200' : ''}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div>
                      <Icon className="w-10 h-10 text-gold-500 mb-4" strokeWidth={1.5} />
                      <h2 className="font-serif text-3xl text-navy-900 mb-2">{sector.label}</h2>
                      {sector.example && <p className="text-gray-500 font-sans text-sm leading-relaxed italic mt-4 border-l-2 border-gold-500 pl-4">"{sector.example}"</p>}
                    </div>
                    <div>
                      <h3 className="font-sans text-xs uppercase tracking-[0.2em] text-gold-500 font-semibold mb-4">Industry Challenges</h3>
                      <div className="space-y-3">
                        {(sector.challenges || []).map((c, j) => <div key={j} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-2 flex-shrink-0" /><span className="text-gray-600 font-sans text-sm">{c}</span></div>)}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-sans text-xs uppercase tracking-[0.2em] text-gold-500 font-semibold mb-4">Our Solutions</h3>
                      <div className="space-y-3">
                        {(sector.solutions || []).map((s, j) => <div key={j} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-gold-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} /><span className="text-gray-700 font-sans text-sm">{s}</span></div>)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-navy-900 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <h2 className="font-serif text-4xl sm:text-5xl text-white font-light tracking-tight mb-6">Don't see your sector?</h2>
          <p className="text-white/60 font-sans mb-10 max-w-xl mx-auto">Our advisory capabilities extend across all industries. Get in touch to discuss how we can help.</p>
          <Link to="/contact" data-testid="sectors-cta-button" className="inline-flex items-center gap-2 bg-gold-500 text-navy-900 px-8 py-4 rounded-sm font-sans text-sm font-medium hover:bg-gold-400 transition-colors duration-300">Start a Conversation <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
}
